/**
 * Repository Provider
 * Provides Effect Runtime context to React components
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import * as Runtime from 'effect/Runtime';
import { getRepositoryRuntimeAsync } from '@/hooks/repository/use-repository-runtime';

interface RepositoryContextValue {
  runtime: Runtime.Runtime<any> | null;
  isReady: boolean;
}

const RepositoryContext = createContext<RepositoryContextValue>({
  runtime: null,
  isReady: false,
});

/**
 * Hook to access the repository runtime
 */
export function useRepositoryContext() {
  const context = useContext(RepositoryContext);
  if (!context.isReady) {
    throw new Error('Repository runtime not initialized. Wrap your app in RepositoryProvider.');
  }
  return context;
}

interface RepositoryProviderProps {
  children: React.ReactNode;
}

/**
 * Repository Provider Component
 * Initializes the Effect runtime and provides it to child components
 */
export function RepositoryProvider({ children }: RepositoryProviderProps) {
  const [runtime, setRuntime] = useState<Runtime.Runtime<any> | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize runtime on mount
    getRepositoryRuntimeAsync()
      .then(newRuntime => {
        setRuntime(newRuntime);
        setIsReady(true);
      })
      .catch(error => {
        console.error('[RepositoryProvider] Failed to initialize runtime:', error);
        // Could show error UI here
      });

    // Cleanup on unmount
    return () => {
      // Runtime cleanup if needed
      setRuntime(null);
      setIsReady(false);
    };
  }, []);

  if (!isReady) {
    // You could show a loading spinner here
    return null;
  }

  return (
    <RepositoryContext.Provider value={{ runtime, isReady }}>
      {children}
    </RepositoryContext.Provider>
  );
}
